/**
 * BasketballTracking.tsx - 篮球投篮轨迹追踪
 * 
 * 基于 YouTube 教程（https://www.youtube.com/watch?v=A8miHLSf_BI）设计理念实现
 * 核心功能：
 * - 投篮轨迹动画（抛物线）
 * - 投篮点标注（命中/未命中）
 * - 命中率统计
 * - 球场俯视图
 * 
 * 技术要点：
 * - SVG 轨迹绘制
 * - Spring 动画
 * - 数据驱动可视化
 * - interpolate 插值计算
 */

import React from 'react';
import { AbsoluteFill, useCurrentFrame, interpolate, spring, useVideoConfig } from 'remotion';

interface Shot {
  x: number;
  y: number;
  isMade: boolean;
  playerNumber: number;
}

const shotData: Shot[] = [
  { x: 25, y: 85, isMade: true, playerNumber: 23 },
  { x: 30, y: 90, isMade: false, playerNumber: 23 },
  { x: 75, y: 88, isMade: true, playerNumber: 11 },
  { x: 50, y: 95, isMade: true, playerNumber: 23 },
  { x: 35, y: 80, isMade: false, playerNumber: 11 },
  { x: 65, y: 92, isMade: true, playerNumber: 23 },
  { x: 40, y: 85, isMade: true, playerNumber: 11 },
  { x: 55, y: 88, isMade: false, playerNumber: 23 },
];

const COLORS = {
  court: '#1B5E20',
  courtDark: '#0D3B13',
  lines: 'rgba(255, 255, 255, 0.85)',
  made: '#4CAF50',
  madeGlow: '#81C784',
  missed: '#EF5350',
  missedGlow: '#E57373',
  text: '#ffffff',
  accent: '#FF9800',
  accentLight: '#FFB74D',
  panel: 'rgba(0, 0, 0, 0.75)',
  subtext: '#B0BEC5',
  fontFamily: "'SF Pro Display', 'Helvetica Neue', Arial, sans-serif",
};

// 分阶段时间线（帧数）
const PHASE = {
  courtIn: 0,
  titleIn: 10,
  shotsStart: 30,
  trajectoriesStart: 60,
  statsIn: 50,
  playerStatsIn: 70,
};

const BasketballCourt: React.FC = () => (
  <svg
    width="100%"
    height="100%"
    viewBox="0 0 100 100"
    style={{ position: 'absolute' }}
  >
    <defs>
      <radialGradient id="courtGrad" cx="50%" cy="50%" r="60%">
        <stop offset="0%" stopColor={COLORS.court} />
        <stop offset="100%" stopColor={COLORS.courtDark} />
      </radialGradient>
    </defs>

    <rect x="0" y="0" width="100" height="100" fill="url(#courtGrad)" rx="2" />

    {/* 边线 */}
    <rect x="2" y="2" width="96" height="96" fill="none" stroke={COLORS.lines} strokeWidth="0.4" rx="1" />

    {/* 中线 */}
    <line x1="2" y1="50" x2="98" y2="50" stroke={COLORS.lines} strokeWidth="0.4" />

    {/* 中圈 */}
    <circle cx="50" cy="50" r="10" fill="none" stroke={COLORS.lines} strokeWidth="0.4" />
    <circle cx="50" cy="50" r="1" fill={COLORS.lines} opacity={0.5} />

    {/* ---- 上半场 ---- */}
    {/* 三分线：直线部分 + 弧线 */}
    <line x1="14" y1="2" x2="14" y2="14" stroke={COLORS.lines} strokeWidth="0.4" />
    <line x1="86" y1="2" x2="86" y2="14" stroke={COLORS.lines} strokeWidth="0.4" />
    <path d="M 14 14 A 36 36 0 0 0 86 14" fill="none" stroke={COLORS.lines} strokeWidth="0.4" />

    {/* 禁区 */}
    <rect x="30" y="2" width="40" height="19" fill="none" stroke={COLORS.lines} strokeWidth="0.4" />
    {/* 罚球线半圆 */}
    <path d="M 30 21 A 10 10 0 0 0 70 21" fill="none" stroke={COLORS.lines} strokeWidth="0.4" />
    {/* 限制区弧线 */}
    <path d="M 40 2 A 8 8 0 0 0 60 2" fill="none" stroke={COLORS.lines} strokeWidth="0.3" strokeDasharray="1 1" />

    {/* 篮筐 */}
    <rect x="47" y="2" width="6" height="2.5" fill="none" stroke={COLORS.accentLight} strokeWidth="0.3" />
    <circle cx="50" cy="6" r="1.5" fill="none" stroke={COLORS.accent} strokeWidth="0.6" />

    {/* ---- 下半场 ---- */}
    <line x1="14" y1="98" x2="14" y2="86" stroke={COLORS.lines} strokeWidth="0.4" />
    <line x1="86" y1="98" x2="86" y2="86" stroke={COLORS.lines} strokeWidth="0.4" />
    <path d="M 14 86 A 36 36 0 0 1 86 86" fill="none" stroke={COLORS.lines} strokeWidth="0.4" />

    <rect x="30" y="79" width="40" height="19" fill="none" stroke={COLORS.lines} strokeWidth="0.4" />
    <path d="M 30 79 A 10 10 0 0 1 70 79" fill="none" stroke={COLORS.lines} strokeWidth="0.4" />
    <path d="M 40 98 A 8 8 0 0 1 60 98" fill="none" stroke={COLORS.lines} strokeWidth="0.3" strokeDasharray="1 1" />

    <rect x="47" y="95.5" width="6" height="2.5" fill="none" stroke={COLORS.accentLight} strokeWidth="0.3" />
    <circle cx="50" cy="94" r="1.5" fill="none" stroke={COLORS.accent} strokeWidth="0.6" />
  </svg>
);

const ShotPointsLayer: React.FC<{
  shots: Shot[];
  frame: number;
  fps: number;
}> = ({ shots, frame, fps }) => (
  <svg
    width="100%"
    height="100%"
    viewBox="0 0 100 100"
    style={{ position: 'absolute', pointerEvents: 'none' }}
  >
    <defs>
      <filter id="glow-green" x="-50%" y="-50%" width="200%" height="200%">
        <feGaussianBlur stdDeviation="1.2" result="blur" />
        <feMerge>
          <feMergeNode in="blur" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
      <filter id="glow-red" x="-50%" y="-50%" width="200%" height="200%">
        <feGaussianBlur stdDeviation="0.8" result="blur" />
        <feMerge>
          <feMergeNode in="blur" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
    </defs>

    {shots.map((shot, index) => {
      const startFrame = PHASE.shotsStart + index * 8;
      const opacity = interpolate(
        frame - startFrame,
        [0, 12],
        [0, 1],
        { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
      );
      const scale = spring({
        frame: Math.max(0, frame - startFrame),
        fps,
        config: { damping: 12, stiffness: 150, mass: 0.6 },
      });

      const color = shot.isMade ? COLORS.made : COLORS.missed;
      const glowColor = shot.isMade ? COLORS.madeGlow : COLORS.missedGlow;
      const filterId = shot.isMade ? 'glow-green' : 'glow-red';

      const pulseScale = shot.isMade
        ? 1 + 0.15 * Math.sin((frame - startFrame) * 0.12)
        : 1;

      return (
        <g
          key={index}
          opacity={opacity}
          transform={`translate(${shot.x}, ${shot.y}) scale(${scale * pulseScale}) translate(${-shot.x}, ${-shot.y})`}
          filter={`url(#${filterId})`}
        >
          <circle cx={shot.x} cy={shot.y} r={3.5} fill={glowColor} opacity={0.25} />
          <circle
            cx={shot.x}
            cy={shot.y}
            r={2}
            fill={color}
            stroke="rgba(255,255,255,0.6)"
            strokeWidth="0.3"
          />
          {shot.isMade ? (
            <text
              x={shot.x}
              y={shot.y + 0.6}
              textAnchor="middle"
              fontSize="1.6"
              fill={COLORS.text}
              fontWeight="bold"
              fontFamily={COLORS.fontFamily}
            >
              {shot.playerNumber}
            </text>
          ) : (
            <>
              <line
                x1={shot.x - 0.9}
                y1={shot.y - 0.9}
                x2={shot.x + 0.9}
                y2={shot.y + 0.9}
                stroke="rgba(255,255,255,0.8)"
                strokeWidth="0.35"
                strokeLinecap="round"
              />
              <line
                x1={shot.x + 0.9}
                y1={shot.y - 0.9}
                x2={shot.x - 0.9}
                y2={shot.y + 0.9}
                stroke="rgba(255,255,255,0.8)"
                strokeWidth="0.35"
                strokeLinecap="round"
              />
            </>
          )}
        </g>
      );
    })}
  </svg>
);

/**
 * 对二次贝塞尔曲线做 0→t 的分段截取
 * 利用 De Casteljau 算法得到新的控制点
 */
function splitQuadBezier(
  p0x: number, p0y: number,
  cpx: number, cpy: number,
  p2x: number, p2y: number,
  t: number,
) {
  const q0x = p0x + (cpx - p0x) * t;
  const q0y = p0y + (cpy - p0y) * t;
  const q1x = cpx + (p2x - cpx) * t;
  const q1y = cpy + (p2y - cpy) * t;
  const bx = q0x + (q1x - q0x) * t;
  const by = q0y + (q1y - q0y) * t;
  return { cp: { x: q0x, y: q0y }, end: { x: bx, y: by } };
}

const ShotTrajectory: React.FC<{
  shot: Shot;
  index: number;
  frame: number;
  fps: number;
}> = ({ shot, index, frame, fps }) => {
  const delay = PHASE.trajectoriesStart + index * 35;
  const duration = 40;

  const progress = interpolate(
    frame - delay,
    [0, duration],
    [0, 1],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
  );

  if (progress <= 0) return null;

  const startX = shot.x;
  const startY = 50;
  const endX = shot.x;
  const endY = shot.y;

  const controlX = (startX + endX) / 2 + (index % 2 === 0 ? 8 : -8);
  const controlY = Math.min(startY, endY) - 18;

  const { cp, end } = splitQuadBezier(startX, startY, controlX, controlY, endX, endY, progress);

  const ballRotation = progress * 720;

  const trailOpacity = interpolate(progress, [0, 0.3, 1], [0.6, 0.4, 0.15], {
    extrapolateRight: 'clamp',
  });

  return (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 100 100"
      style={{ position: 'absolute', pointerEvents: 'none' }}
    >
      <defs>
        <radialGradient id={`ball-grad-${index}`} cx="35%" cy="35%" r="65%">
          <stop offset="0%" stopColor="#FFB74D" />
          <stop offset="100%" stopColor="#E65100" />
        </radialGradient>
      </defs>

      {/* 轨迹虚线 */}
      <path
        d={`M ${startX} ${startY} Q ${cp.x} ${cp.y} ${end.x} ${end.y}`}
        fill="none"
        stroke={COLORS.accent}
        strokeWidth="0.4"
        strokeDasharray="1.5 1"
        opacity={trailOpacity}
      />

      {/* 篮球 */}
      <g transform={`translate(${end.x}, ${end.y})`}>
        <circle r={2.2} fill={`url(#ball-grad-${index})`} />
        <circle r={2.2} fill="none" stroke="rgba(0,0,0,0.3)" strokeWidth="0.15" />
        {/* 十字纹 */}
        <g transform={`rotate(${ballRotation})`} opacity={0.45}>
          <path d="M -2.2 0 A 2.2 2.2 0 0 1 2.2 0" fill="none" stroke="#3E2723" strokeWidth="0.25" />
          <line x1="0" y1="-2.2" x2="0" y2="2.2" stroke="#3E2723" strokeWidth="0.25" />
        </g>
      </g>
    </svg>
  );
};

const ShootingStats: React.FC<{
  frame: number;
  fps: number;
  shots: Shot[];
}> = ({ frame, fps, shots }) => {
  const totalShots = shots.length;
  const madeShots = shots.filter(s => s.isMade).length;
  const shootingPercentage = Math.round((madeShots / totalShots) * 100);

  const enter = spring({
    frame: Math.max(0, frame - PHASE.statsIn),
    fps,
    config: { damping: 14, stiffness: 120 },
  });

  const displayPercentage = interpolate(
    frame - PHASE.statsIn,
    [0, 50],
    [0, shootingPercentage],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
  );

  const barWidth = interpolate(
    frame - PHASE.statsIn,
    [10, 60],
    [0, 100],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
  );

  return (
    <div
      style={{
        position: 'absolute',
        top: 40,
        right: 40,
        opacity: enter,
        transform: `translateX(${(1 - enter) * 30}px)`,
        backgroundColor: COLORS.panel,
        backdropFilter: 'blur(10px)',
        padding: '24px 36px',
        borderRadius: 12,
        borderLeft: `4px solid ${COLORS.accent}`,
        fontFamily: COLORS.fontFamily,
        minWidth: 220,
      }}
    >
      <div
        style={{
          fontSize: 20,
          color: COLORS.accent,
          fontWeight: 600,
          marginBottom: 6,
          letterSpacing: 1,
          textTransform: 'uppercase',
        }}
      >
        Shot Accuracy
      </div>

      <div
        style={{
          fontSize: 64,
          color: COLORS.text,
          fontWeight: 'bold',
          fontFamily: "'SF Mono', 'Fira Code', monospace",
          lineHeight: 1.1,
        }}
      >
        {Math.round(displayPercentage)}
        <span style={{ fontSize: 50, color: COLORS.subtext }}>%</span>
      </div>

      {/* 进度条 */}
      <div
        style={{
          width: '100%',
          height: 6,
          backgroundColor: 'rgba(255,255,255,0.1)',
          borderRadius: 3,
          marginTop: 8,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${(barWidth / 100) * shootingPercentage}%`,
            height: '100%',
            background: `linear-gradient(90deg, ${COLORS.made}, ${COLORS.accent})`,
            borderRadius: 3,
          }}
        />
      </div>

      <div
        style={{
          fontSize: 32,
          color: COLORS.subtext,
          marginTop: 12,
        }}
      >
        {madeShots} / {totalShots} 命中
      </div>

      {/* 图例 */}
      <div
        style={{
          display: 'flex',
          gap: 20,
          marginTop: 14,
          fontSize: 22,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div
            style={{
              width: 20,
              height: 20,
              borderRadius: '50%',
              backgroundColor: COLORS.made,
              boxShadow: `0 0 6px ${COLORS.made}`,
            }}
          />
          <span style={{ color: COLORS.text }}>命中</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div
            style={{
              width: 20,
              height: 20,
              borderRadius: '50%',
              backgroundColor: COLORS.missed,
              boxShadow: `0 0 6px ${COLORS.missed}`,
            }}
          />
          <span style={{ color: COLORS.text }}>未命中</span>
        </div>
      </div>
    </div>
  );
};

const PlayerStats: React.FC<{
  frame: number;
  fps: number;
  shots: Shot[];
}> = ({ frame, fps, shots }) => {
  const playerStats = shots.reduce((acc, shot) => {
    if (!acc[shot.playerNumber]) {
      acc[shot.playerNumber] = { made: 0, total: 0 };
    }
    acc[shot.playerNumber].total++;
    if (shot.isMade) acc[shot.playerNumber].made++;
    return acc;
  }, {} as Record<number, { made: number; total: number }>);

  const enter = spring({
    frame: Math.max(0, frame - PHASE.playerStatsIn),
    fps,
    config: { damping: 14, stiffness: 120 },
  });

  return (
    <div
      style={{
        position: 'absolute',
        bottom: 50,
        left: 40,
        opacity: enter,
        transform: `translateY(${(1 - enter) * 20}px)`,
        backgroundColor: COLORS.panel,
        backdropFilter: 'blur(10px)',
        padding: '20px 28px',
        borderRadius: 12,
        borderLeft: `4px solid ${COLORS.accent}`,
        fontFamily: COLORS.fontFamily,
      }}
    >
      <div
        style={{
          fontSize: 24,
          color: COLORS.accent,
          fontWeight: 600,
          marginBottom: 14,
          letterSpacing: 1,
          textTransform: 'uppercase',
        }}
      >
        Player Stats
      </div>

      {Object.entries(playerStats).map(([number, stats], index) => {
        const percentage = Math.round((stats.made / stats.total) * 100);
        const rowDelay = PHASE.playerStatsIn + 10 + index * 12;
        const rowEnter = spring({
          frame: Math.max(0, frame - rowDelay),
          fps,
          config: { damping: 14, stiffness: 150 },
        });

        return (
          <div
            key={number}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              marginBottom: 12,
              opacity: rowEnter,
              transform: `translateX(${(1 - rowEnter) * 15}px)`,
            }}
          >
            <div
              style={{
                width: 50,
                height: 50,
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.accentLight})`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 20,
                fontWeight: 'bold',
                color: '#1a1a1a',
                boxShadow: `0 2px 8px rgba(255,152,0,0.3)`,
              }}
            >
              #{number}
            </div>

            <div>
              <div style={{ fontSize: 24, color: COLORS.text, fontWeight: 500 }}>
                {stats.made}/{stats.total} 命中
              </div>
              {/* 迷你进度条 */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  marginTop: 3,
                }}
              >
                <div
                  style={{
                    width: 80,
                    height: 4,
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    borderRadius: 2,
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      width: `${percentage * rowEnter}%`,
                      height: '100%',
                      background: percentage >= 50
                        ? `linear-gradient(90deg, ${COLORS.made}, ${COLORS.madeGlow})`
                        : `linear-gradient(90deg, ${COLORS.missed}, ${COLORS.missedGlow})`,
                      borderRadius: 2,
                    }}
                  />
                </div>
                <span style={{ fontSize: 28, color: COLORS.subtext, fontFamily: "'SF Mono', monospace" }}>
                  {percentage}%
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export const BasketballTracking: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const courtEnter = spring({
    frame: Math.max(0, frame - PHASE.courtIn),
    fps,
    config: { damping: 14, stiffness: 100 },
  });

  const titleEnter = spring({
    frame: Math.max(0, frame - PHASE.titleIn),
    fps,
    config: { damping: 14, stiffness: 120 },
  });

  return (
    <AbsoluteFill
      style={{
        backgroundColor: '#0a0a0a',
        fontFamily: COLORS.fontFamily,
      }}
    >
      {/* 背景微光 */}
      <div
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          background: 'radial-gradient(ellipse at 40% 50%, rgba(27,94,32,0.15) 0%, transparent 70%)',
        }}
      />

      {/* 标题 */}
      <div
        style={{
          position: 'absolute',
          top: 40,
          left: 40,
          zIndex: 10,
          opacity: titleEnter,
          transform: `translateY(${(1 - titleEnter) * -20}px)`,
        }}
      >
        <div
          style={{
            fontSize: 44,
            fontWeight: 'bold',
            color: COLORS.text,
            letterSpacing: -0.5,
          }}
        >
          投篮轨迹追踪
        </div>
        <div
          style={{
            fontSize: 24,
            color: COLORS.subtext,
            marginTop: 6,
            fontWeight: 300,
            letterSpacing: 2,
            textTransform: 'uppercase',
          }}
        >
          Basketball Shot Tracking
        </div>
      </div>

      {/* 球场 */}
      <div
        style={{
          position: 'absolute',
          width: '55%',
          height: '78%',
          left: '20%',
          top: '14%',
          opacity: courtEnter * 0.9,
          transform: `scale(${0.95 + courtEnter * 0.05})`,
          transformOrigin: 'center center',
          borderRadius: 8,
          overflow: 'hidden',
          boxShadow: '0 4px 30px rgba(0,0,0,0.5)',
        }}
      >
        <BasketballCourt />
        <ShotPointsLayer shots={shotData} frame={frame} fps={fps} />

        {shotData.slice(0, 3).map((shot, index) => (
          <ShotTrajectory
            key={`traj-${index}`}
            shot={shot}
            index={index}
            frame={frame}
            fps={fps}
          />
        ))}
      </div>

      <ShootingStats frame={frame} fps={fps} shots={shotData} />
      <PlayerStats frame={frame} fps={fps} shots={shotData} />

      {/* 底部水印 */}
      <div
        style={{
          position: 'absolute',
          bottom: 16,
          right: 40,
          fontSize: 13,
          color: 'rgba(255,255,255,0.2)',
          letterSpacing: 1,
          opacity: courtEnter,
        }}
      >
        Powered by Remotion
      </div>
    </AbsoluteFill>
  );
};
